import React, { useState, useRef, useEffect } from 'react';
import './AutomationPage.css';
import { Calendar, Clock, Users, Instagram, Zap, X, Plus, CheckCircle2, AlertCircle, Play, Pause, MoreVertical, Pencil, Trash2, TrendingUp, TrendingDown, Palette, Star, Heart } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Select } from '@/components/ui/Select';
import { Search } from 'lucide-react';
import { useOplaes } from '@/providers/OplaesProvider';
import BuildFromScratchModal from './modals/BuildFromScratchModal';

// Reuse the AutomationCard from AutomationLibrary
const AutomationCard = ({ automation, onEdit, onDelete, onTogglePause }) => {
  const [showDetails, setShowDetails] = useState(false);
  const {
    id,
    title,
    description,
    creator,
    color,
    goal,
    engagement_rate,
    strategy,
    videos = [],
    tasks = [],
    metrics,
    tags = [],
    isCustomizable
  } = automation;

  const progressPercent = goal ? (videos.length / goal.videos_count) * 100 : 0;

  return (
    <>
      <div 
        className="automation-card cursor-pointer"
        onClick={() => setShowDetails(true)}
      >
        <div className="card-content">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-1.5">
                <Badge variant="premium" className="badge">
                  Pro
                </Badge>
                {isCustomizable && (
                  <Badge className="badge badge-customizable">
                    Customizable
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-gray-300">
                <Heart className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">{metrics?.likes || "348"}</span>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-100">
                {title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">
                {description}
              </p>
              {tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag, index) => (
                    <Badge 
                      key={index}
                      className="badge"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/90" />
                </div>
                <span className="text-xs text-gray-300">{creator}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-400">{metrics?.activeUsers || "1.2K"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-xs text-green-400">{metrics?.engagementLift || "+45%"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <OplaeDetailsModal 
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        oplae={automation}
      />
    </>
  );
};

const MetricCard = ({ label, value, className }) => (
  <div className="flex flex-col items-center">
    <div className={cn("text-sm font-medium", className)}>{value}</div>
    <div className="text-[10px] text-gray-500 mt-0.5">{label}</div>
  </div>
);

const BRAND_COLORS = [
  { name: 'Periwinkle', value: '#818cf8' },   // Soft indigo
  { name: 'Lavender', value: '#a78bfa' },     // Soft purple
  { name: 'Rose', value: '#f472b6' },         // Soft pink
  { name: 'Sky', value: '#60a5fa' },          // Soft blue
  { name: 'Aqua', value: '#67e8f9' },         // Soft cyan
  { name: 'Mint', value: '#5eead4' },         // Soft teal
  { name: 'Sage', value: '#86efac' },         // Soft green
  { name: 'Peach', value: '#fdba74' },        // Soft orange
  { name: 'Coral', value: '#fca5a5' },        // Soft red
  { name: 'Lilac', value: '#c4b5fd' },        // Soft violet
];

const CreateAutomationModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    timing: 'ai',
    goal: 'reach',
    platforms: ['instagram'],
    color: '#6366f1', // Default indigo color
    schedule: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        timing: 'ai',
        goal: 'reach',
        platforms: ['instagram'],
        color: '#6366f1',
        schedule: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      });
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      alert('Please enter a name for your automation');
      return;
    }
    
    // Validate schedule if custom timing is selected
    if (formData.timing === 'custom') {
      const hasTimeSlots = Object.values(formData.schedule).some(slots => slots.length > 0);
      if (!hasTimeSlots) {
        alert('Please set at least one time slot for custom scheduling');
        return;
      }
    }
    
    onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] w-[calc(100%-32px)] p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">Create New Automation</DialogTitle>
        </DialogHeader>
        <div className="py-4 overflow-auto max-h-[calc(100vh-200px)] sm:max-h-[600px]">
          <div className="space-y-6 sm:space-y-8">
            {/* Name Input */}
            <div className="space-y-3">
              <label 
                htmlFor="automation-name" 
                className="text-sm sm:text-base font-medium text-gray-700"
              >
                Automation Name
              </label>
              <input
                id="automation-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Peak Hours Posting"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         text-sm sm:text-base"
              />
            </div>

            <SchedulingRules 
              selectedTiming={formData.timing}
              onSelect={(timing) => setFormData({ ...formData, timing })}
              schedule={formData.schedule}
              onScheduleChange={(schedule) => setFormData({ ...formData, schedule })}
            />

            {/* Color Picker */}
            <div className="space-y-3">
              <label 
                className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700"
              >
                <Palette className="h-4 w-4" />
                Calendar Color
              </label>
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-3">
                  {BRAND_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={cn(
                        "w-10 h-10 rounded-full transition-all duration-200",
                        "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2",
                        "flex items-center justify-center",
                        formData.color === color.value ? "ring-2 ring-offset-2" : ""
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    >
                      {formData.color === color.value && (
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      )}
                    </button>
                  ))}
                </div>
                <span className="block text-sm text-gray-500">
                  Choose a color to identify this automation's posts in the calendar view
                </span>
              </div>
            </div>

            {/* Show Goal selection only if AI timing is selected */}
            {formData.timing === 'ai' && (
              <GoalDropdown 
                selectedGoal={formData.goal}
                onSelect={(goal) => setFormData({ ...formData, goal })}
              />
            )}

            <PlatformSelection 
              selectedPlatforms={formData.platforms}
              onSelect={(platforms) => setFormData({ ...formData, platforms })}
            />
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={onClose} 
                className="text-sm rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-sm rounded-xl"
                onClick={handleSubmit}
                disabled={!formData.name.trim()}
              >
                Create Automation
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SchedulingRules = ({ selectedTiming, onSelect, schedule, onScheduleChange }) => (
  <div className="scheduling-rules">
    <div className="section-header">
      <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
      <h3 className="text-sm sm:text-base">When to Post</h3>
    </div>
    <div className="rule-options">
      <label className="rule-option">
        <input 
          type="radio" 
          name="timing" 
          checked={selectedTiming === 'ai'}
          onChange={() => onSelect('ai')}
        />
        <div className="rule-content">
          <span className="rule-title text-sm sm:text-base">AI-Powered Timing</span>
          <span className="rule-description text-xs sm:text-sm">Let AI determine the best times based on your audience's activity</span>
        </div>
      </label>
      <label className="rule-option">
        <input 
          type="radio" 
          name="timing"
          checked={selectedTiming === 'custom'}
          onChange={() => onSelect('custom')}
        />
        <div className="rule-content">
          <span className="rule-title text-sm sm:text-base">Custom Schedule</span>
          <span className="rule-description text-xs sm:text-sm">Set your own posting schedule</span>
        </div>
      </label>
    </div>
    {selectedTiming === 'custom' && (
      <CustomScheduleSettings 
        schedule={schedule}
        onScheduleChange={onScheduleChange}
      />
    )}
  </div>
);

const GoalDropdown = ({ selectedGoal, onSelect }) => (
  <div className="goal-selector">
    <div className="flex items-center gap-2 mb-3 sm:mb-4">
      <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
      <h2 className="text-sm sm:text-base font-medium text-gray-700">What's your goal?</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
      <button 
        className={cn("goal-option", selectedGoal === 'reach' && "active")}
        onClick={() => onSelect('reach')}
      >
        <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm">Maximize Reach</span>
      </button>
      <button 
        className={cn("goal-option", selectedGoal === 'engagement' && "active")}
        onClick={() => onSelect('engagement')}
      >
        <Users className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm">Engage Audience</span>
      </button>
      <button 
        className={cn("goal-option", selectedGoal === 'consistency' && "active")}
        onClick={() => onSelect('consistency')}
      >
        <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm">Consistent Posting</span>
      </button>
    </div>
  </div>
);

const CustomScheduleSettings = ({ schedule, onScheduleChange }) => {
  const addTimeSlot = (day) => {
    onScheduleChange({
      ...schedule,
      [day]: [...schedule[day], { time: "12:00", period: "PM" }]
    });
  };

  const removeTimeSlot = (day, index) => {
    onScheduleChange({
      ...schedule,
      [day]: schedule[day].filter((_, i) => i !== index)
    });
  };

  const updateTimeSlot = (day, index, time, period) => {
    onScheduleChange({
      ...schedule,
      [day]: schedule[day].map((slot, i) => 
        i === index ? { time, period } : slot
      )
    });
  };

  const days = [
    'monday', 'tuesday', 'wednesday', 'thursday',
    'friday', 'saturday', 'sunday'
  ];

  const timeOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = (i + 1).toString().padStart(2, '0');
    return [
      `${hour}:00`,
      `${hour}:30`
    ];
  }).flat();

  return (
    <div className="mt-6 space-y-4 border rounded-lg p-4 bg-white/50 backdrop-blur-sm">
      <div className="text-sm font-medium text-gray-700 mb-4">
        Choose your posting times for each day
      </div>
      
      <div className="space-y-4">
        {days.map(day => (
          <div key={day} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium capitalize">{day}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addTimeSlot(day)}
                className="h-7 text-xs rounded-xl"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Time
              </Button>
            </div>
            
            {schedule[day].length === 0 ? (
              <div className="text-xs text-gray-500 italic">
                No times set for {day}
              </div>
            ) : (
              <div className="space-y-2">
                {schedule[day].map((slot, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <select
                      value={slot.time}
                      onChange={(e) => updateTimeSlot(day, index, e.target.value, slot.period)}
                      className="text-sm p-1 border rounded"
                    >
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    <select
                      value={slot.period}
                      onChange={(e) => updateTimeSlot(day, index, slot.time, e.target.value)}
                      className="text-sm p-1 border rounded"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTimeSlot(day, index)}
                      className="h-7 w-7 p-0 rounded-xl"
                    >
                      <X className="h-3 w-3 text-gray-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const PlatformSelection = ({ selectedPlatforms, onSelect }) => (
  <div className="platform-selection">
    <div className="space-y-2">
      <div className="section-header">
        <Instagram className="h-4 w-4 sm:h-5 sm:w-5" />
        <h3 className="text-sm sm:text-base">Platforms</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">
        Select the social media platforms where you'd like your content to be automatically shared. Your posts will be optimized for each platform's specific format and requirements.
      </p>
    </div>
    <div className="platform-options mt-4">
      <label className="platform-option">
        <input 
          type="checkbox" 
          checked={selectedPlatforms.includes('instagram')}
          onChange={(e) => {
            if (e.target.checked) {
              onSelect([...selectedPlatforms, 'instagram']);
            } else {
              onSelect(selectedPlatforms.filter(p => p !== 'instagram'));
            }
          }}
        />
        <div className="platform-content">
          <span className="platform-name text-sm sm:text-base">Instagram</span>
          <span className="platform-stats text-xs sm:text-sm">12M+ followers</span>
        </div>
      </label>
      <label className="platform-option">
        <input 
          type="checkbox"
          checked={selectedPlatforms.includes('tiktok')}
          onChange={(e) => {
            if (e.target.checked) {
              onSelect([...selectedPlatforms, 'tiktok']);
            } else {
              onSelect(selectedPlatforms.filter(p => p !== 'tiktok'));
            }
          }}
        />
        <div className="platform-content">
          <span className="platform-name text-sm sm:text-base">TikTok</span>
          <span className="platform-stats text-xs sm:text-sm">5M+ followers</span>
        </div>
      </label>
    </div>
  </div>
);

// iOS-specific breakpoints and constants
const IOS_BREAKPOINTS = {
  iPhone: '390px',    // iPhone 12/13/14
  iPhonePlus: '428px' // iPhone Pro Max
};

const IOS_SAFE_AREAS = {
  top: 'env(safe-area-inset-top, 47px)',      // Default notch height
  bottom: 'env(safe-area-inset-bottom, 34px)', // Default home indicator height
};

// Enhanced animations and transitions
const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: SPRING_CONFIG
  }
};

// Enhanced marketplace search and filter components
const SearchBar = () => {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        placeholder="Search Oplaes by name, creator, or tag..."
        className={cn(
          "block w-full pl-10 pr-4 py-2",
          "bg-gray-800/50 border border-gray-700 rounded-xl",
          "text-gray-100 placeholder-gray-500",
          "focus:outline-none focus:ring-2 focus:ring-purple-500/50",
          "transition-colors duration-200"
        )}
      />
    </div>
  );
};

const FilterSection = ({ onFilterChange }) => {
  const filterOptions = {
    categories: [
          { value: 'all', label: 'All Categories' },
          { value: 'growth', label: 'Growth & Reach' },
          { value: 'engagement', label: 'Engagement' },
          { value: 'sync', label: 'Creator Sync' }
    ],
    sort: [
          { value: 'trending', label: 'Trending' },
          { value: 'newest', label: 'Newest' },
          { value: 'popular', label: 'Most Popular' }
    ],
    platforms: [
          { value: 'all', label: 'All Platforms' },
          { value: 'instagram', label: 'Instagram' },
          { value: 'tiktok', label: 'TikTok' }
    ]
  };

  return (
    <div className="flex flex-wrap gap-3">
      {Object.entries(filterOptions).map(([key, options]) => (
        <Select 
          key={key}
          value={options[0].value}
          onValueChange={(value) => onFilterChange?.({ [key]: value })}
          options={options}
          className={cn(
            "bg-gray-800/50 border-gray-700 text-gray-300",
            "rounded-xl transition-colors duration-200",
            "hover:bg-gray-800/70"
          )}
        />
      ))}
    </div>
  );
};

const OplaeDetailsModal = ({ isOpen, onClose, oplae }) => {
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [customizationStep, setCustomizationStep] = useState('initial');
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  if (!oplae) return null;

  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
    setCustomizationStep('creator-search');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!rounded-2xl sm:max-w-[800px] w-[calc(100%-32px)] p-0 bg-gradient-to-br from-gray-900/90 to-gray-800/90 border border-gray-800/50 overflow-hidden">
        <div className="relative rounded-2xl overflow-hidden">
          {/* Top Bar - Glassmorphism */}
          <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-sm border-b border-gray-700/30">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-100">
                {oplae.title}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6 space-y-6">
            {/* Title and Description */}
            <div className="space-y-2">
              <p className="text-gray-400 text-sm leading-relaxed">
                {oplae.description}
              </p>
            </div>

            {/* Platform Selection Step */}
            {oplae.isCustomizable && oplae.title === "Creator Sync Pro" && customizationStep === 'platform-select' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-200">
                  Select Platform
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {oplae.platforms.map(platform => (
                    <button
                      key={platform}
                      onClick={() => handlePlatformSelect(platform)}
                      className={cn(
                        "flex items-center gap-3 p-4",
                        "rounded-xl border transition-all duration-200",
                        "hover:bg-gray-800/50",
                        "group"
                      )}
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 
                                    flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-pink-500/30">
                        {platform === 'instagram' ? (
                          <Instagram className="w-6 h-6 text-gray-300" />
                        ) : (
                          <span className="text-gray-300 text-lg font-medium">TT</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-gray-200 font-medium capitalize mb-1">
                          {platform}
                        </h4>
                        <p className="text-gray-500 text-sm">
                          Sync with top {platform} creators
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Creator Search Step */}
            {oplae.isCustomizable && oplae.title === "Creator Sync Pro" && customizationStep === 'creator-search' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-200">
                    Select Creator to Sync With
                  </h3>
                  <Badge className="bg-gray-800 text-gray-400 capitalize">
                    {selectedPlatform}
                  </Badge>
                </div>
                <CreatorSearch 
                  platform={selectedPlatform} 
                  onSelect={setSelectedCreator}
                />
              </div>
            )}

            {/* Performance Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard
                label="Active Users"
                value={oplae.metrics.activeUsers}
                className="text-gray-200"
              />
              <MetricCard
                label="Engagement"
                value={oplae.metrics.engagementLift}
                className="text-green-400"
              />
              <MetricCard
                label="Success Rate"
                value={oplae.metrics.successRate}
                className="text-gray-200"
              />
              <MetricCard
                label="Response Time"
                value={oplae.metrics.responseTime}
                className="text-green-400"
              />
            </div>
          </div>

          {/* Bottom Bar - Glassmorphism */}
          <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 
                        backdrop-blur-sm border-t border-gray-700/30">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white/90" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-300">{oplae.creator}</span>
                  <span className="text-[10px] text-gray-500">Verified Creator</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {oplae.isCustomizable && (
                  <>
                    {customizationStep === 'initial' ? (
                      <Button 
                        onClick={() => {
                          setCustomizationStep('platform-select');
                          setSelectedCreator(null);
                          setSelectedPlatform(null);
                        }}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600
                                  text-white text-sm px-6 py-2 rounded-xl"
                      >
                        Customize
                      </Button>
                    ) : (
                      <>
                        <Button 
                          onClick={() => {
                            if (customizationStep === 'creator-search') {
                              setCustomizationStep('platform-select');
                              setSelectedCreator(null);
                            } else {
                              setCustomizationStep('initial');
                              setSelectedPlatform(null);
                            }
                          }}
                          className="bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-700 hover:to-gray-600
                                    text-white text-sm px-6 py-2 rounded-xl"
                        >
                          Back
                        </Button>
                        {customizationStep === 'creator-search' && (
                          <Button 
                            onClick={() => {
                              // Handle creator sync setup
                              if (selectedCreator) {
                                // Implement creator sync logic
                                onClose();
                              }
                            }}
                            disabled={!selectedCreator}
                            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600
                                      text-white text-sm px-6 py-2 rounded-xl disabled:opacity-50"
                          >
                            Start Syncing
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
                {!oplae.isCustomizable && (
                  <Button 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600
                              text-white text-sm px-6 py-2 rounded-xl"
                  >
                    Use This Oplae
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Add this new component after AutomationCard
const ComingSoonCard = ({ automation }) => {
  const [isJoined, setIsJoined] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleJoinWaitlist = () => {
    if (isJoined) return;
    
    setIsAnimating(true);
    setTimeout(() => {
      setIsJoined(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 1500);
    }, 600);
  };

  return (
    <div className="coming-soon-card">
      <div className="card-content">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge className="badge">Coming Soon</Badge>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-100">
              {automation.title}
            </h3>
            <p className="text-sm text-gray-400">
              Launching {automation.launchDate}
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              {automation.description}
            </p>
            {automation.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {automation.tags.map((tag, index) => (
                  <Badge 
                    key={index}
                    className="badge"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">{automation.waitlistCount}+ joined waitlist</span>
            </div>
            <button
              onClick={handleJoinWaitlist}
              disabled={isJoined}
              className={cn(
                "card-button relative overflow-hidden",
                isJoined && "!bg-green-500"
              )}
            >
              <span className={cn(
                "inline-flex items-center gap-2",
                "transition-all duration-300",
                isAnimating ? "opacity-0" : "opacity-100"
              )}>
                {isJoined ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Joined Waitlist
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Join Waitlist
                  </>
                )}
              </span>
              {isAnimating && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreatorSearch = ({ platform, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const recommendedCreators = [
    {
      id: 'rec1',
      username: 'techie_sarah',
      displayName: 'Sarah\'s Tech Tips',
      followers: '1.2M',
      niche: 'Tech Reviews',
      platform: 'instagram',
      avatar: 'https://example.com/avatar1.jpg'
    },
    {
      id: 'rec2',
      username: 'startup_mike',
      displayName: 'Mike | Startup Tips',
      followers: '850K',
      niche: 'Business',
      platform: 'instagram',
      avatar: 'https://example.com/avatar2.jpg'
    },
    {
      id: 'rec3',
      username: 'fitness_pro',
      displayName: 'FitnessPro Tips',
      followers: '2.1M',
      niche: 'Fitness',
      platform: 'tiktok',
      avatar: 'https://example.com/avatar3.jpg'
    }
  ];

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setLoading(true);
    
    // Simulate API call to search creators
    setTimeout(() => {
      const filtered = recommendedCreators.filter(creator => 
        creator.username.toLowerCase().includes(query.toLowerCase()) ||
        creator.displayName.toLowerCase().includes(query.toLowerCase()) ||
        creator.niche.toLowerCase().includes(query.toLowerCase())
      );
      setCreators(filtered);
      setLoading(false);
    }, 500);
  };

  const handleCreatorSelect = (creator) => {
    setSelectedCreator(creator);
    onSelect?.(creator);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search creators by username or niche..."
          className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700/50 
                    rounded-xl text-gray-100 placeholder-gray-500
                    focus:outline-none focus:ring-2 focus:ring-purple-500/50"
        />
      </div>

      {/* Recommended/Search Results */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-400">
          {searchQuery ? 'Search Results' : 'Recommended Creators'}
        </h4>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-6 h-6 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {(searchQuery ? creators : recommendedCreators).map(creator => (
              <button
                key={creator.id}
                onClick={() => handleCreatorSelect(creator)}
                className={cn(
                  "w-full flex items-center gap-3 p-3",
                  "rounded-xl border transition-all duration-200",
                  "hover:bg-gray-800/50",
                  selectedCreator?.id === creator.id
                    ? "border-purple-500/50 bg-purple-500/10"
                    : "border-gray-700/50 bg-gray-800/30"
                )}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 
                              flex items-center justify-center">
                  {creator.platform === 'instagram' ? (
                    <Instagram className="w-5 h-5 text-gray-300" />
                  ) : (
                    <span className="text-gray-300 text-sm">TT</span>
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-200 font-medium">{creator.username}</span>
                    <Badge className="bg-gray-800 text-gray-400 text-xs">
                      {creator.niche}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-500">
                    {creator.followers} followers
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BuildFromScratchCard = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div 
        className="build-card cursor-pointer"
        onClick={() => setShowModal(true)}
      >
        <div className="build-card-content">
          <div className="build-card-left">
            <div className="build-card-icon">
              <Plus className="w-6 h-6 text-purple-400" />
            </div>
            <div className="build-card-text">
              <h3 className="text-xl font-semibold text-gray-100">
                Build from Scratch
              </h3>
              <p className="text-gray-400 text-sm">
                Create a custom automation workflow tailored to your needs
              </p>
            </div>
          </div>
          
          <div className="build-card-right">
            <div className="build-card-stat">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">5-10 min setup</span>
            </div>
            <button className="card-button">
              <Plus className="w-4 h-4" />
              <span>Start Fresh</span>
            </button>
          </div>
        </div>
      </div>

      <BuildFromScratchModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

// Main AutomationPage Component
const AutomationPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    sort: 'trending',
    platform: 'all'
  });
  
  const pullToRefreshRef = useRef(null);
  const startY = useRef(0);
  
  const {
    oplaes = { active: [], inactive: [] }, // Provide default value
    loading,
    error,
    fetchOplaes,
    createOplae,
    updateOplae,
    activateOplae,
    deactivateOplae,
    destroyOplae,
    clearError
  } = useOplaes();

  const handleFilterChange = (newFilter) => {
    setFilters(prev => ({
      ...prev,
      ...newFilter
    }));

    // Filter the marketplace data based on the new filters
    const filteredData = {
      featured: filterMarketplaceData(marketplaceData.featured, { ...filters, ...newFilter }),
      trending: filterMarketplaceData(marketplaceData.trending, { ...filters, ...newFilter }),
      newArrivals: filterMarketplaceData(marketplaceData.newArrivals, { ...filters, ...newFilter })
    };

    setMarketplaceData(prev => ({
      ...prev,
      ...filteredData
    }));
  };

  const filterMarketplaceData = (data = [], filters) => {
    if (!data) return [];
    
    return data.filter(item => {
      // Category filter
      if (filters.category !== 'all' && !item.tags.includes(filters.category)) {
        return false;
      }

      // Platform filter
      if (filters.platform !== 'all' && !item.platforms.includes(filters.platform)) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      // Sort filter
      switch (filters.sort) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'popular':
          return b.metrics.activeUsers - a.metrics.activeUsers;
        case 'trending':
        default:
          return b.metrics.engagementLift.replace('%', '') - a.metrics.engagementLift.replace('%', '');
      }
    });
  };

  const filteredAutomations = (oplaes?.active || []).filter(automation => {
    if (selectedPlatform === 'all') return true;
    return automation.videos.some(video => {
      return selectedPlatform === 'instagram' ? 
        video.target_uid.includes('instagram') : 
        video.source_uid.includes('tiktok');
    });
  });

  const handleEdit = async (automation) => {
    try {
      await updateOplae(automation.id, {
        title: automation.title,
        color: automation.color,
        strategy: automation.strategy,
        goal: automation.goal,
        tasks: automation.tasks
      });
    } catch (error) {
      console.error('Failed to update automation:', error);
    }
  };

  const handleDelete = async (automation) => {
    if (window.confirm('Are you sure you want to delete this automation?')) {
      try {
        await destroyOplae(automation.id);
      } catch (error) {
        console.error('Failed to delete automation:', error);
      }
    }
  };

  const handleTogglePause = async (automation) => {
    try {
      if (automation.active) {
        await deactivateOplae(automation.id);
      } else {
        await activateOplae(automation.id);
      }
    } catch (error) {
      console.error('Failed to toggle automation status:', error);
    }
  };

  const handleCreateAutomation = async (formData) => {
    try {
      await createOplae({
        title: formData.name,
        color: formData.color,
        strategy: formData.timing === 'ai' ? 'AI-Powered' : 'Custom Schedule',
        goal: {
          cadence: formData.timing === 'ai' ? formData.goal : 'custom',
          start_date: new Date().toISOString(),
          videos_count: 2
        },
        tasks: []
      });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create automation:', error);
    }
  };

  // Pull-to-refresh logic
  useEffect(() => {
    fetchOplaes();
  }, [fetchOplaes]);

  useEffect(() => {
    const element = pullToRefreshRef.current;
    
    const handleTouchStart = (e) => {
      startY.current = e.touches[0].pageY;
    };

    const handleTouchMove = async (e) => {
      const currentY = e.touches[0].pageY;
      const diff = currentY - startY.current;
      
      if (diff > 50 && window.scrollY === 0 && !refreshing) {
        setRefreshing(true);
        try {
          await fetchOplaes();
        } catch (error) {
          console.error('Failed to refresh automations:', error);
        } finally {
          setRefreshing(false);
        }
      }
    };

    if (element) {
      element.addEventListener('touchstart', handleTouchStart);
      element.addEventListener('touchmove', handleTouchMove);
    }

    return () => {
      if (element) {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [refreshing, fetchOplaes]);

  // Update the marketplace data to include coming soon items
  const [marketplaceData, setMarketplaceData] = useState({
    featured: [
      {
        id: 1,
        title: "Viral Creator Sync",
        name: "Viral Creator Sync",
        description: "Automatically sync your content with top creators in your niche. This Oplae monitors selected creators and helps you post similar content at optimal times.",
        creator: "Growth Team",
        isCustomizable: true,
        tags: ["Trending", "Creator Sync", "Growth"],
        platforms: ["tiktok", "instagram"],
        metrics: {
          engagementLift: "+45%",
          successRate: "98%",
          responseTime: "< 5 min",
          activeUsers: "1.2K",
          rating: "4.8",
          reviewCount: "156"
        }
      },
      {
        id: 2,
        title: "Smart Engagement Booster",
        name: "Smart Engagement Booster",
        description: "Uses AI to analyze your audience's behavior and automatically posts when they're most active. Includes smart caption generation and hashtag optimization.",
        creator: "AI Labs",
        isCustomizable: false,
        tags: ["AI-Powered", "Engagement", "Analytics"],
        platforms: ["instagram"],
        metrics: {
          engagementLift: "+38%",
          successRate: "96%",
          responseTime: "Real-time",
          activeUsers: "2.3K",
          rating: "4.8",
          reviewCount: "156"
        }
      },
      {
        id: 3,
        title: "Creator Sync Pro",
        name: "Creator Sync Pro",
        description: "Automatically sync your posting schedule with top creators in your niche. Find and follow successful creators' posting patterns to maximize your content's impact.",
        creator: "SyncMaster",
        isCustomizable: true,
        tags: ["Creator Sync", "Timing", "Growth"],
        platforms: ["instagram", "tiktok"],
        metrics: {
          engagementLift: "+62%",
          successRate: "95%",
          responseTime: "Real-time",
          activeUsers: "2.8K",
          rating: "4.9",
          reviewCount: "189"
        }
      },
      {
        id: 4,
        title: "Content Remix AI",
        name: "Content Remix AI",
        description: "AI-powered content repurposing tool that automatically transforms your best performing posts into new formats with fresh perspectives and trending elements.",
        creator: "Creative AI",
        isCustomizable: true,
        tags: ["AI-Powered", "Content", "Repurposing"],
        platforms: ["tiktok", "instagram"],
        metrics: {
          engagementLift: "+41%",
          successRate: "97%",
          responseTime: "Real-time",
          activeUsers: "1.8K",
          rating: "4.7",
          reviewCount: "142"
        }
      }
    ],
    comingSoon: [
      {
        id: 'cs-1',
        title: "AI Content Calendar",
        description: "Revolutionary AI-powered content calendar that learns from your best performing posts and automatically suggests optimal posting schedules.",
        launchDate: "December 2023",
        waitlistCount: "2.4K",
        tags: ["AI-Powered", "Planning", "Analytics"],
        platforms: ["instagram", "tiktok"]
      },
      {
        id: 'cs-2',
        title: "Cross-Platform Analytics",
        description: "Get deep insights into your content performance across all platforms with advanced analytics and AI-powered recommendations.",
        launchDate: "January 2024",
        waitlistCount: "1.8K",
        tags: ["Analytics", "Multi-Platform", "Insights"],
        platforms: ["instagram", "tiktok"]
      }
    ],
    trending: [/* ... */],
    newArrivals: [/* ... */]
  });

  return (
    <div className={cn(
      "min-h-screen bg-[#0B0F15]",
      "pt-[var(--navbar-height,_76px)]",
      "pb-[env(safe-area-inset-bottom,_0px)]",
      "overflow-x-hidden"
    )}>
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="bg-[#151A21] p-3 border-b border-gray-800">
          <div className="max-w-full px-5 pb-2">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 text-transparent bg-clip-text mb-2">
              Oplae Community
            </h1>
            <p className="text-gray-300 text-base">
              Discover and use powerful automation workflows created by top creators
            </p>
          </div>
        </div>

        <div className="px-4 sm:px-6 md:px-8 pb-12">
          {/* Filters */}
          <div className="mb-6">
            <FilterSection onFilterChange={handleFilterChange} />
          </div>

          {/* Build from Scratch - Now full width */}
          <div className="mb-8">
            <BuildFromScratchCard />
          </div>

          <div className="space-y-8">
            {/* Featured Oplaes */}
            <section>
              <h2 className="text-xl font-semibold text-gray-100 mb-3">
                Featured Oplaes
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {marketplaceData.featured.map(oplae => (
                  <AutomationCard
                    key={oplae.id}
                    automation={oplae}
                  />
                ))}
              </div>
            </section>

            {/* Coming Soon */}
            <section>
              <h2 className="text-xl font-semibold text-gray-100 mb-3">
                Coming Soon
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {marketplaceData.comingSoon.map(oplae => (
                  <ComingSoonCard
                    key={oplae.id}
                    automation={oplae}
                  />
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <CreateAutomationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateAutomation}
      />

      {error && (
        <div className="px-4 py-2 bg-red-500/10 text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={clearError}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default AutomationPage; 